import { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import { getActiveUsers } from "../../services/shareResourceService";
import { UserDto } from "../../types/User";

export interface WorkflowActiveUser {
  userId: number;
  userName: string;
  /** Shown under the name in the pickers and searched, so two people with the same name can be told apart. */
  email?: string | null;
}

// One list for the whole Builder: the canvas editor can render a picker per task row, and every
// one of them asking for the same list would be a request per row. Cleared on failure so the next
// mount retries.
let cachedUsers: WorkflowActiveUser[] | null = null;
let pendingUsers: Promise<WorkflowActiveUser[] | null> | null = null;

/** Test-only: forget the cached list between tests. */
export const resetActiveUsersCache = () => {
  cachedUsers = null;
  pendingUsers = null;
};

const toActiveUsers = (users: UserDto[]): WorkflowActiveUser[] =>
  users
    .filter((user): user is UserDto & { userId: number } => typeof user.userId === "number")
    .map((user) => ({
      userId: user.userId,
      userName: user.userName ?? `User #${user.userId}`,
      email: user.cachedEmailAddress?.trim() || null,
    }));

/** Active Portal users, for the Builder's person pickers (SME, owner, approvers, assignees). */
export const useActiveUsers = (enabled = true) => {
  const { executeApi } = useApi();
  const [users, setUsers] = useState<WorkflowActiveUser[]>(cachedUsers ?? []);
  // Set once this hook's own request has answered (even with a failure).
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!enabled || cachedUsers) return undefined;
    let active = true;
    pendingUsers ??= executeApi<string, UserDto[]>(getActiveUsers, "").then((list) => {
      pendingUsers = null;
      if (!list) return null;
      cachedUsers = toActiveUsers(list);
      return cachedUsers;
    });
    pendingUsers.then((list) => {
      if (!active) return;
      setUsers(list ?? []);
      setSettled(true);
    });
    return () => {
      active = false;
    };
  }, [enabled, executeApi]);

  // Derived rather than set in the effect, so the render that flips `enabled` on already reports
  // loading (a caller waiting on it - the lock-holder toast - must not act on a stale `false`).
  // The cache is read directly in case another picker filled it after this hook mounted.
  return { users: cachedUsers ?? users, loading: enabled && !cachedUsers && !settled };
};
